import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, CreditCard, PackageOpen, Truck, CheckCircle2, XCircle, MapPin, Search, Loader2 } from "lucide-react";
import { api, fmt, errMsg, mediaUrl } from "../lib/api";
import { useAuth } from "../context/Store";

const STEPS = [
  { key: "CREATED", label: "Order placed", desc: "We received your order", Icon: Package },
  { key: "PAID", label: "Payment confirmed", desc: "Payment received securely", Icon: CreditCard },
  { key: "PROCESSING", label: "Packing", desc: "Your order is being prepared", Icon: PackageOpen },
  { key: "SHIPPED", label: "Shipped", desc: "On its way to you", Icon: Truck },
  { key: "DELIVERED", label: "Delivered", desc: "Enjoy your purchase", Icon: CheckCircle2 },
];

function LookupForm({ initialOrderId, onFound }) {
  const [orderId, setOrderId] = useState(initialOrderId || "");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.get("/public/track", { params: { order_id: orderId.trim(), phone: phone.trim() } });
      onFound(data);
    } catch (err) {
      import("sonner").then(({ toast }) => toast.error(errMsg(err, "Could not find that order")));
    } finally {
      setBusy(false);
    }
  };

  const input = "mt-2 w-full h-12 rounded-2xl bg-brand-ink/5 px-4 text-sm outline-none focus:ring-2 focus:ring-brand-cyan";

  return (
    <form onSubmit={submit} className="mt-10 max-w-md rounded-3xl border border-brand-ink/10 p-8" data-testid="public-track-form">
      <h2 className="font-display text-xl font-bold">Find your order</h2>
      <p className="text-sm text-brand-ink/50 mt-2">Enter your order ID and the phone number used at checkout. No sign-in needed.</p>
      <div className="mt-6 space-y-4">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-brand-ink/50">Order ID</label>
          <input data-testid="track-input-order" required value={orderId} onChange={(e) => setOrderId(e.target.value.toUpperCase())} placeholder="VNR..." className={input} />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-brand-ink/50">Phone number</label>
          <input data-testid="track-input-phone" required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="9790007557" className={input} />
        </div>
        <button type="submit" disabled={busy} data-testid="track-lookup-button"
          className="w-full h-12 rounded-full brand-gradient-bg text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Track my order
        </button>
      </div>
    </form>
  );
}

export default function TrackOrder() {
  const { orderId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [needLookup, setNeedLookup] = useState(!orderId);

  useEffect(() => {
    if (!orderId) return;
    if (user) {
      api.get(`/orders/${orderId}/track`)
        .then((r) => { setData(r.data); setNeedLookup(false); })
        .catch(() => setNeedLookup(true));
    } else if (user === null) {
      setNeedLookup(true);
    }
  }, [user, orderId]);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-28 md:pt-36 pb-24" data-testid="track-page">
      <p className="text-xs uppercase tracking-[0.25em] text-brand-blue font-semibold">Order tracking</p>

      {needLookup && !data && (
        <>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3">Track your order</h1>
          <LookupForm initialOrderId={orderId} onFound={setData} />
        </>
      )}

      {!needLookup && !data && <div className="pt-20 text-brand-ink/40">Loading...</div>}

      {data && <Timeline data={data} />}
    </div>
  );
}

function Timeline({ data }) {
  const doneKeys = new Set(data.history.map((h) => h.status));
  const timeOf = (key) => data.history.find((h) => h.status === key)?.at;
  const currentIdx = data.status === "CANCELLED" ? -1 : STEPS.findIndex((s) => s.key === data.status);
  const eta = data.paid_at ? new Date(new Date(data.paid_at).getTime() + 5 * 86400000) : null;

  return (
    <>
      <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3 font-mono" data-testid="track-order-id">{data.order_id}</h1>
      <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-brand-ink/50">
        <span>Placed {new Date(data.created_at).toLocaleString("en-IN")}</span>
        <span>·</span>
        <span className="font-semibold text-brand-ink">{fmt(data.total)}</span>
      </div>

      {data.status === "CANCELLED" ? (
        <div className="mt-10 rounded-3xl bg-rose-50 border border-rose-200 p-8 text-center" data-testid="track-cancelled">
          <XCircle className="h-10 w-10 text-rose-500 mx-auto" />
          <p className="font-display text-xl font-bold mt-4 text-rose-700">This order was cancelled</p>
          <p className="text-sm text-rose-500 mt-2">Any amount paid is refunded to the source within 5-7 working days.</p>
        </div>
      ) : (
        <div className="mt-12" data-testid="track-timeline">
          {STEPS.map((s, i) => {
            const done = doneKeys.has(s.key);
            const current = i === currentIdx && !doneKeys.has("DELIVERED");
            const at = timeOf(s.key);
            return (
              <motion.div key={s.key} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.12, duration: 0.5 }} className="flex gap-5">
                <div className="flex flex-col items-center">
                  <div className={`h-12 w-12 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                    done ? "brand-gradient-bg text-white shadow-[0_0_20px_rgba(0,229,255,0.4)]"
                      : current ? "bg-white border-2 border-brand-cyan text-brand-blue animate-pulse"
                      : "bg-brand-ink/5 text-brand-ink/25"}`}>
                    <s.Icon className="h-5 w-5" />
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`w-0.5 flex-1 min-h-8 my-1 ${doneKeys.has(STEPS[i + 1].key) ? "brand-gradient-bg" : "bg-brand-ink/10"}`} />
                  )}
                </div>
                <div className="pb-8">
                  <p className={`font-display font-bold ${done || current ? "text-brand-ink" : "text-brand-ink/30"}`}
                    data-testid={`track-step-${s.key.toLowerCase()}`}>{s.label}</p>
                  <p className={`text-sm ${done || current ? "text-brand-ink/50" : "text-brand-ink/25"}`}>{s.desc}</p>
                  {at && <p className="text-xs text-brand-blue mt-1 font-medium">{new Date(at).toLocaleString("en-IN")}</p>}
                  {s.key === "SHIPPED" && eta && doneKeys.has("SHIPPED") && !doneKeys.has("DELIVERED") && (
                    <p className="text-xs text-brand-ink/40 mt-1">Expected by {eta.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</p>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="rounded-3xl bg-brand-ink/[0.03] p-6">
          <h3 className="font-display font-bold text-sm uppercase tracking-wider text-brand-ink/50">Items</h3>
          <div className="mt-4 space-y-3">
            {data.items.map((i) => (
              <div key={i.product_id} className="flex items-center gap-3 text-sm">
                <img src={mediaUrl(i.image)} alt="" className="h-11 w-11 rounded-xl object-cover" />
                <span className="flex-1 min-w-0 truncate font-medium">{i.name} × {i.qty}</span>
                <span className="font-semibold">{fmt(i.price * i.qty)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-3xl bg-brand-ink/[0.03] p-6">
          <h3 className="font-display font-bold text-sm uppercase tracking-wider text-brand-ink/50 flex items-center gap-2">
            <MapPin className="h-4 w-4" /> Delivering to
          </h3>
          <p className="mt-4 text-sm text-brand-ink/70 leading-relaxed">
            <span className="font-semibold text-brand-ink">{data.address.name}</span><br />
            {data.address.line && <>{data.address.line}<br /></>}
            {data.address.city}{data.address.state ? `, ${data.address.state}` : ""}{data.address.pincode ? ` — ${data.address.pincode}` : ""}
            {data.address.phone && <><br />{data.address.phone}</>}
          </p>
        </div>
      </div>
      <div className="mt-10">
        <Link to="/account" className="text-sm font-semibold text-brand-blue">← All orders</Link>
      </div>
    </>
  );
}
